import type {
  ApiError,
  ChatMessage,
  ChatResponse,
  StartersResponse,
} from "@/types/api";
import { platformAuthHeaders } from "@/lib/platformAuth";

const DEFAULT_TOP_K = 6;

async function parseError(response: Response): Promise<ApiError> {
  try {
    const text = await response.text();
    if (!text) return { error: `HTTP ${response.status}` };
    const data = JSON.parse(text) as ApiError;
    return {
      error: data.error || `HTTP ${response.status}`,
      detail: data.detail,
      requestId: data.requestId,
    };
  } catch {
    return { error: `HTTP ${response.status}` };
  }
}

/**
 * Auth headers for API calls.
 * - Teams: Authorization Bearer (Entra / Easy Auth)
 * - Platform Hub: X-NDC-Access-Token (WorkOS — custom header so Easy Auth
 *   does not reject a non-Entra Bearer)
 * - Direct browser: Easy Auth session cookie (no explicit header)
 */
function authHeaders(teamsToken?: string | null): Record<string, string> {
  if (teamsToken) return { Authorization: `Bearer ${teamsToken}` };
  return platformAuthHeaders();
}

export async function fetchStarters(
  signal?: AbortSignal,
  teamsToken?: string | null,
): Promise<string[]> {
  const response = await fetch("/api/starters", {
    signal,
    credentials: "include",
    headers: { ...authHeaders(teamsToken) },
  });
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
  const data = (await response.json()) as StartersResponse;
  return Array.isArray(data.starters) ? data.starters : [];
}

export interface SendChatArgs {
  message: string;
  history: ChatMessage[];
  topK?: number;
  signal?: AbortSignal;
  teamsToken?: string | null;
}

export async function sendChat({
  message,
  history,
  topK = DEFAULT_TOP_K,
  signal,
  teamsToken,
}: SendChatArgs): Promise<ChatResponse> {
  const response = await fetch("/api/chat", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...authHeaders(teamsToken) },
    body: JSON.stringify({ message, history, topK }),
    signal,
  });

  if (!response.ok) {
    const err = await parseError(response);
    const error = new Error(err.error) as Error & { requestId?: string };
    error.requestId = err.requestId;
    throw error;
  }

  return (await response.json()) as ChatResponse;
}

export interface FeedbackPayload {
  vote: "up" | "down";
  requestId?: string | null;
  messageId?: string | null;
  question?: string | null;
  reason?: string | null;
}

export async function submitFeedback(
  payload: FeedbackPayload,
  teamsToken?: string | null,
): Promise<void> {
  try {
    await fetch("/api/feedback", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(teamsToken) },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    /* feedback is best-effort; silently swallow */
  }
}

export interface BugReportPayload {
  description: string;
  context?: string;
  question?: string;
  timestamp: string;
}

export interface SuggestionPayload {
  description: string;
  category?: "ui" | "answer-quality" | "feature-request" | "bug";
  timestamp: string;
}

export interface SuggestionResponse {
  ok: boolean;
  issueId?: string;
  issueUrl?: string;
  /** True when the submission matched an existing idea and was upvoted instead of creating a new ticket. */
  deduplicated?: boolean;
}

export interface PreviewResponse {
  /** AI-written ticket description. Null when a duplicate was detected. */
  polishedDescription: string | null;
  /** Acceptance-criteria bullet points. Empty when a duplicate was detected. */
  keyPoints: string[];
  /** Existing issues that are the same idea as this submission. */
  similarIssues: Array<{ identifier: string; title: string; url: string }>;
}

/**
 * Asks the backend to (a) rewrite the rough description using GPT-4o and
 * (b) search Linear for similar existing ideas. Returns the polished text
 * and any similar issues found for the user to review before submitting.
 */
export async function previewSuggestion(
  description: string,
  teamsToken?: string | null,
): Promise<PreviewResponse> {
  const response = await fetch("/api/suggestions/preview", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(teamsToken) },
    body: JSON.stringify({ description }),
  });
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
  return (await response.json()) as PreviewResponse;
}

/**
 * Submits general staff feedback to the backend, which creates a Linear issue
 * or upvotes an existing one if the idea already exists.
 * Throws on network or server error so the caller can show an error state.
 */
export async function submitSuggestion(
  payload: SuggestionPayload,
  teamsToken?: string | null,
): Promise<SuggestionResponse> {
  const response = await fetch("/api/suggestions", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(teamsToken) },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
  return (await response.json()) as SuggestionResponse;
}

export interface FeedbackReport {
  total: number;
  upCount: number;
  downCount: number;
  upPct: number;
  topDownQuestions: Array<{ question: string; count: number }>;
  note?: string;
  latency?: {
    sampleCount: number;
    medianMs: number;
    p95Ms: number;
    avgMs: number;
  } | null;
}

export async function fetchFeedbackReport(): Promise<FeedbackReport> {
  const response = await fetch("/api/feedback/report");
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
  return (await response.json()) as FeedbackReport;
}

export interface ConversationToolSummary {
  name: string;
  durationMs: number | null;
  error: string | null;
}

export interface ConversationRecord {
  requestId: string;
  question: string;
  answer: string;
  sourceCount: number;
  toolCallCount: number;
  durationMs?: number | null;
  foundryRounds?: number | null;
  toolIterations?: number | null;
  toolSummary?: ConversationToolSummary[];
  timestamp: string;
  userId?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  vote: "up" | "down" | null;
  reason: string | null;
  feedbackAt: string | null;
}

export interface ConversationUserOption {
  userId: string | null;
  userName: string | null;
  userEmail: string | null;
}

export interface ConversationsResponse {
  configured: boolean;
  total: number;
  totalPages: number;
  page: number;
  pageSize: number;
  filter: string;
  user?: string;
  users?: ConversationUserOption[];
  summary: { total: number; upCount: number; downCount: number; noVoteCount: number };
  records: ConversationRecord[];
}

export type ConversationFilter = "all" | "up" | "down" | "none";

export interface PerformanceToolRow {
  name: string;
  callCount: number;
  errorCount: number;
  errorRatePct: number;
  avgDurationMs: number | null;
  p95DurationMs: number | null;
  totalDurationMs: number | null;
}

export interface PerformanceReport {
  configured: boolean;
  sampleCount: number;
  withTimingCount: number;
  latency: {
    sampleCount: number;
    medianMs: number;
    p95Ms: number;
    avgMs: number;
  } | null;
  buckets: Array<{ id: string; label: string; count: number }>;
  tools: PerformanceToolRow[];
  rounds: {
    withFoundryRoundsCount: number;
    avgFoundryRounds: number | null;
    maxFoundryRounds: number | null;
    withToolIterationsCount: number;
    avgToolIterations: number | null;
    maxToolIterations: number | null;
  };
  totals: {
    conversationCount: number;
    totalToolCalls: number;
    toolErrorCount: number;
    toolErrorRatePct: number;
    avgToolsPerConversation: number;
    withSourcesCount: number;
    withSourcesPct: number;
  };
}

export async function fetchPerformanceReport(): Promise<PerformanceReport> {
  const response = await fetch("/api/admin/performance");
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
  return (await response.json()) as PerformanceReport;
}

export async function fetchConversations(
  page = 1,
  filter: ConversationFilter = "all",
  user = "",
): Promise<ConversationsResponse> {
  const params = new URLSearchParams({
    page: String(page),
    vote: filter,
  });
  const trimmedUser = user.trim();
  if (trimmedUser) params.set("user", trimmedUser);
  const response = await fetch(`/api/admin/conversations?${params.toString()}`);
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
  return (await response.json()) as ConversationsResponse;
}

/**
 * Downloads the full conversations+feedback export as a CSV file and
 * triggers a browser save-as, using the page's own auth cookies.
 * Throws (with the server's error message) on a non-2xx response instead of
 * silently downloading an error body as if it were a CSV.
 */
export async function downloadConversationsCsv(): Promise<void> {
  const response = await fetch("/api/admin/export.csv");
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const disposition = response.headers.get("Content-Disposition") || "";
  const match = /filename="([^"]+)"/.exec(disposition);
  a.download = match?.[1] || "ndc-assistant-conversations.csv";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export interface UserConversationSummary {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  turnCount: number;
}

export interface UserConversationDetail<TTurn = unknown, TMessage = unknown>
  extends UserConversationSummary {
  turns: TTurn[];
  history: TMessage[];
}

export type ConversationHistoryLoadResult =
  | { status: "success"; conversations: UserConversationSummary[] }
  | { status: "unauthenticated"; conversations: [] }
  | { status: "server-error"; conversations: []; statusCode: number }
  | { status: "network-error"; conversations: [] };

/**
 * Fetches the signed-in user's saved conversation list (metadata only, no
 * message content). Authentication, server, and network failures remain
 * distinct from a successful empty result so the UI never implies that an
 * outage deleted the user's conversations.
 */
export async function fetchUserConversations(
  teamsToken?: string | null,
): Promise<ConversationHistoryLoadResult> {
  try {
    const response = await fetch("/api/user/conversations", {
      headers: { ...authHeaders(teamsToken) },
    });
    if (response.status === 401) {
      return { status: "unauthenticated", conversations: [] };
    }
    if (!response.ok) {
      return { status: "server-error", conversations: [], statusCode: response.status };
    }
    const data = (await response.json()) as { conversations?: UserConversationSummary[] };
    return {
      status: "success",
      conversations: Array.isArray(data.conversations) ? data.conversations : [],
    };
  } catch {
    return { status: "network-error", conversations: [] };
  }
}

/** Fetches one full saved conversation (including turns/history). Null on failure. */
export async function fetchUserConversation<TTurn = unknown, TMessage = unknown>(
  id: string,
  teamsToken?: string | null,
): Promise<UserConversationDetail<TTurn, TMessage> | null> {
  try {
    const response = await fetch(`/api/user/conversations/${encodeURIComponent(id)}`, {
      headers: { ...authHeaders(teamsToken) },
    });
    if (!response.ok) return null;
    return (await response.json()) as UserConversationDetail<TTurn, TMessage>;
  } catch {
    return null;
  }
}

export interface SaveUserConversationPayload<TTurn, TMessage> {
  id: string;
  title: string;
  turns: TTurn[];
  history: TMessage[];
  createdAt: string;
}

/**
 * Outcome of a conversation save attempt:
 * - "saved"          — the server confirmed it wrote the record to Azure Tables.
 * - "not-configured" — the server has no Azure Storage configured (e.g. local
 *                       dev). Not a failure — the caller should keep showing
 *                       in-memory-only history for this session.
 * - "failed"         — the server rejected the request (e.g. 401 because the
 *                       sign-in session hadn't fully established yet) or the
 *                       request never reached the server. The caller should
 *                       NOT assume the conversation was persisted.
 */
export type SaveConversationResult = "saved" | "not-configured" | "failed";

/**
 * Saves (creates or updates) one conversation thread so it follows the
 * signed-in user across browsers and devices. Never throws — callers use the
 * returned result to decide whether to trust their optimistic UI state.
 */
export async function saveUserConversation<TTurn, TMessage>(
  payload: SaveUserConversationPayload<TTurn, TMessage>,
  teamsToken?: string | null,
): Promise<SaveConversationResult> {
  try {
    const response = await fetch("/api/user/conversations", {
      method: "POST",
      headers: { "Content-Type": "application/json", ...authHeaders(teamsToken) },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (!response.ok) return "failed";
    const data = (await response.json().catch(() => null)) as { saved?: boolean } | null;
    return data?.saved === false ? "not-configured" : "saved";
  } catch {
    return "failed";
  }
}

/**
 * Deletes one saved conversation. Returns whether the server confirmed the
 * delete, so the caller can resync its list if the request was rejected or
 * never reached the server (rather than assuming it worked).
 */
export async function deleteUserConversation(
  id: string,
  teamsToken?: string | null,
): Promise<boolean> {
  try {
    const response = await fetch(`/api/user/conversations/${encodeURIComponent(id)}`, {
      method: "DELETE",
      headers: { ...authHeaders(teamsToken) },
    });
    return response.ok;
  } catch {
    return false;
  }
}

/**
 * Submits a user bug report to the backend, which creates a Linear issue.
 * Throws on network or server error so the caller can show an error state.
 */
export interface MaintenanceNotice {
  id: string;
  title: string;
  body: string;
  tone: string;
  type: string;
  learnMoreUrl?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
}

export interface NotificationItem {
  id: string;
  text: string;
  createdAt: string;
  read: boolean;
  actor?: string | null;
  type?: string;
  href?: string | null;
}

export interface NotificationFeed {
  enabled: boolean;
  unavailable?: boolean;
  productContext: string;
  unreadCount: number;
  items: NotificationItem[];
  viewAllHref?: string | null;
  viewAllLabel?: string | null;
  maintenance: MaintenanceNotice | null;
}

export async function fetchNotifications(
  teamsToken?: string | null,
): Promise<NotificationFeed> {
  const response = await fetch("/api/notifications", {
    credentials: "include",
    headers: { ...authHeaders(teamsToken) },
  });
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
  return (await response.json()) as NotificationFeed;
}

export async function markNotificationRead(
  messageId: string,
  teamsToken?: string | null,
): Promise<NotificationFeed> {
  const response = await fetch("/api/notifications/read", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...authHeaders(teamsToken) },
    body: JSON.stringify({ messageId }),
  });
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
  return (await response.json()) as NotificationFeed;
}

export async function dismissMaintenanceNotice(
  id: string,
  teamsToken?: string | null,
): Promise<NotificationFeed> {
  const response = await fetch("/api/notifications/maintenance/dismiss", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...authHeaders(teamsToken) },
    body: JSON.stringify({ id }),
  });
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
  return (await response.json()) as NotificationFeed;
}

export async function submitBugReport(
  payload: BugReportPayload,
  teamsToken?: string | null,
): Promise<void> {
  const response = await fetch("/api/bug-report", {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders(teamsToken) },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const err = await parseError(response);
    throw new Error(err.error);
  }
}
